import {
  GoodRecommendationOptions,
  RecommendedContactStatusOptions,
  UncoachableReasons,
  ActiveInaccurateContactRecommendationReasons,
  ContactViewTabs,
  ContactStatusOptions,
  BarRaiseChoices,
} from '@amzn/aet-contactrecommendation-kit';

import { convertEnumToObject } from '../../Util';
import {
  AppSyncID,
  AWSDateTime,
  ContactInput,
  defaultContactReviewValues,
  DeliveredStatusType,
  EnumEmpty,
  enumEmpty,
  MetricType,
} from '../CoachingForm/schema';

export type GoodRecommendationOptionsType = string;
export type UncoachableReasonsOptionsType = string;
export type RecommendedContactStatusOptionsType = string;
export type ContactReviewStatusOptionsType = string;
export type ContactStatusOptionsType = string;
export type ContactViewTabType = string;
export type ResolvedProblemOptionsType = string;
export type controllableOptionsType = string;

export const GoodRecommendationOptionsObject = convertEnumToObject(
  GoodRecommendationOptions
);
export const RecommendedContactStatusOptionsObject = convertEnumToObject(
  RecommendedContactStatusOptions
);

export const ContactStatusOptionsObject =
  convertEnumToObject(ContactStatusOptions);

export const UncoachableReasonsObject = convertEnumToObject(UncoachableReasons);
export const InaccurateContactRecommendationReasonsObject = convertEnumToObject(
  ActiveInaccurateContactRecommendationReasons
);
export const ContactViewTabObject = convertEnumToObject(ContactViewTabs);

export interface FormAnswer {
  status?: string | EnumEmpty;
  note?: string;
}
export interface ContactDetails extends ContactInput {
  goodRecommendation: GoodRecommendationOptionsType | EnumEmpty;
  uncoachableReason: UncoachableReasonsOptionsType | EnumEmpty;
  uncoachableReasonText?: string;
  resolvedProblem?: string | EnumEmpty;
  controllable?: string | EnumEmpty;
  uncontrollableText?: string;
  greet?: FormAnswer;
  verifySecurity?: FormAnswer;
  identifyProblem?: FormAnswer;
  acknowledge?: FormAnswer;
  researchMatch?: FormAnswer;
  solve?: FormAnswer;
  close?: FormAnswer;
  lastModifiedDate?: string;
  lastModifiedUser?: string;
  additionalComment?: string;
  ingress?: string;
}

const defaultFormAnswer: FormAnswer = {
  status: BarRaiseChoices[BarRaiseChoices.NOT_APPLICABLE],
  note: '',
};

export const defaultContactDetails: ContactDetails = {
  ...defaultContactReviewValues,
  goodRecommendation: enumEmpty,
  uncoachableReason: enumEmpty,
  uncoachableReasonText: '',
  relatedMetric: '',
  controllable: enumEmpty,
  resolvedProblem: enumEmpty,
  uncontrollableText: '',
  greet: defaultFormAnswer,
  verifySecurity: defaultFormAnswer,
  identifyProblem: defaultFormAnswer,
  acknowledge: defaultFormAnswer,
  researchMatch: defaultFormAnswer,
  solve: defaultFormAnswer,
  close: defaultFormAnswer,
  lastModifiedDate: '',
  lastModifiedUser: '',
  additionalComment: '',
};

export type FollowedSecurityPolicyOptionsType = string;
export type VerticalType = string;

export interface ContactDetailsInput {
  goodRecommendation?: GoodRecommendationOptionsType | EnumEmpty;
  uncoachableReason?: UncoachableReasonsOptionsType | EnumEmpty;
  uncoachableReasonText?: string;
  justification?: string;
  followedSecurityPolicies?: FollowedSecurityPolicyOptionsType | EnumEmpty;
  content?: string;
  vertical?: VerticalType | EnumEmpty;
  relatedMetric?: MetricType | EnumEmpty;
  saved?: number;
  saveOrigin?: number;
  recommended?: number;
  contactTab?: ContactViewTabType | EnumEmpty;
  resolvedProblem?: ResolvedProblemOptionsType | EnumEmpty;
  controllable?: controllableOptionsType | EnumEmpty;
  uncontrollableText?: string;
  greet?: FormAnswer | EnumEmpty;
  verifySecurity?: FormAnswer | EnumEmpty;
  identifyProblem?: FormAnswer | EnumEmpty;
  acknowledge?: FormAnswer | EnumEmpty;
  researchMatch?: FormAnswer | EnumEmpty;
  solve?: FormAnswer | EnumEmpty;
  close?: FormAnswer | EnumEmpty;
  additionalComment?: string;
  contactDate?: string;
  ingress?: string;
}

export interface ContactReviewPageInput extends ContactDetailsInput {
  contactStatus?: ContactReviewStatusOptionsType;
  cscContactID?: string;
}

export interface DeliverDetails {
  deliverStatus: DeliveredStatusType;
  deliverDate: AWSDateTime;
  noteID: string;
}

export interface ContactStatusChangeInput {
  userName: string;
  userID: string;
  year: number;
  week: number;
  contactID: string;
  reviewStatus: RecommendedContactStatusOptionsType;
  contactDetailsInput: ContactDetailsInput;
  contactStatus?: ContactReviewStatusOptionsType;
  coach?: string;
  locale?: string;
  inAppFlag?: boolean;
  emailFlag?: boolean;
}
export interface ContactRecommendationsData {
  contactID: AppSyncID;
  marketplaceID: string;
  marketplaceDomainName?: string;
  contactDate: AWSDateTime;
  sic?: string;
  relatedMetric?: string;
  skill: string;
  category: string;
  displayOrder: number;
  calWeek: number;
  calYear: number;
  status: RecommendedContactStatusOptionsType;
  contactJointID: string;
  contactJointIndex: string;
  amznHierarchy: string;
  contactInfo?: ContactDetails | EnumEmpty;
  deliverInfo?: DeliverDetails | EnumEmpty;
  formReviews?: FormDetails[];
}

export interface CapFields {
  contactWeek?: string;
  auditWeek?: string;
  site?: string;
  flaggedForAbuse?: string;
  handledDate?: string;
  tm?: string;
  contactType?: string;
  agentLoginName?: string;
  rsprPolicyRecommendation?: string;
  rsprUtilization?: string;
  sic1?: string;
  sic2?: string;
  sic3?: string;
  sic4?: string;
  sic1_2?: string;
}

export interface BaseContact {
  contactID: string;
  contactDate: string;
}

export interface ConsolidatedContact extends BaseContact {
  contactID: string;
  userID: string;
  marketplaceID: string;
  marketplaceDomainName?: string;
  status: ContactStatusOptionsType;
  calYear: number;
  calWeek: number;
  contactDate: string;
  sic?: string;
  skill?: string;
  channel?: string;
  ownership?: string;
  duration?: string;
  category?: string;
  displayOrder?: Number;
  deliverInfo?: DeliverDetails;
  contactInfo?: ContactDetails;
  recommended: number;
  recent: number;
  saved: number;
  saveOrigin: number;
  contactStatus?: string;
  processName?: string;
  concessionFeedbackCommentText?: string;
  formReviews?: FormDetails[];
  auditStartDate?: string;
  auditEndDate?: string;
  contactDeliveredUsername?: string;
  capFields?: CapFields;
}

export interface FormDetails {
  formID: string;
  version: string;
  formAnswer: DynamicFormAnswer[];
  contactStatus?: string;
}

export interface DynamicFormAnswer {
  questionID: string;
  questionText: I18NContent;
  type: string;
  selectedOption?: I18NContent;
  answerText?: string;
}

export interface I18NContent {
  translationID: string;
  defaultMessage: string;
}
